/////////////////////////////////////////////////////////
// ethy ecolabels                                        //
//                                                        //
// The official ethy ecolabel marks and the logic that   //
// decides which of them apply to a given listing.        //
/////////////////////////////////////////////////////////

// The badge artwork. Filenames are the canonical ecolabel "keys" — the same
// slugs that are ticked/crossed on a listing in Sharetribe. Keeping the key
// equal to the filename makes the mapping obvious and easy to extend: drop a
// new SVG in assets/ecolabels and add one row below.
import recycledPackaging from '../assets/ecolabels/95recycledpackaging.svg';
import industriallyCompostable from '../assets/ecolabels/industriallycompostablerange.svg';
import homeCompostable from '../assets/ecolabels/homecompostablerange.svg';
import upcycled from '../assets/ecolabels/upcycledproducts.svg';
import refillable from '../assets/ecolabels/refillableproducts.svg';
import fightsFoodWaste from '../assets/ecolabels/fightsfoodwaste.svg';
import plasticFree from '../assets/ecolabels/plasticfreeproducts.svg';
import carbonMeasured from '../assets/ecolabels/carbonmeasured.svg';
import vegan from '../assets/ecolabels/veganrange.svg';
import femaleLed from '../assets/ecolabels/femaleled.svg';
import fairtrade from '../assets/ecolabels/fairtradecertified.svg';
import organic from '../assets/ecolabels/organicrange.svg';
import waterless from '../assets/ecolabels/waterlessproducts.svg';
import ukMade from '../assets/ecolabels/ukmade.svg';
import circularEconomy from '../assets/ecolabels/circulareconomy.svg';

import { constructQueryParamName } from '../util/search';

/**
 * The full set of ethy ecolabels, in the order they should be displayed.
 * - key:     canonical slug (used internally + for the badge artwork filename)
 * - label:   human-readable name (used for alt text / tooltips)
 * - src:     the badge artwork
 * - options: the ACTUAL option value(s) used by the hosted `ecolabels`
 *            multi-enum listing field in Sharetribe. These are short slugs
 *            (e.g. `vegan`, `plastic-free`) that differ from our canonical key,
 *            so they must be listed explicitly — this is exactly what the badges
 *            were failing to match on before.
 *
 * The live field is:
 *   { key: 'ecolabels', schemaType: 'multi-enum', enumOptions: [{ option, label }] }
 * and a listing stores the ticked options as e.g.
 *   publicData.ecolabels = ['vegan', 'plastic-free', 'uk']
 */
export const ECOLABELS = [
  {
    key: '95recycledpackaging',
    label: '95% recycled packaging',
    src: recycledPackaging,
    options: ['recycled'],
  },
  {
    key: 'industriallycompostablerange',
    label: 'Industrially compostable range',
    src: industriallyCompostable,
    options: ['indust-compost'],
  },
  {
    key: 'homecompostablerange',
    label: 'Home compostable range',
    src: homeCompostable,
    options: ['home-compost'],
  },
  { key: 'upcycledproducts', label: 'Upcycled products', src: upcycled, options: ['upcycle'] },
  { key: 'refillableproducts', label: 'Refillable products', src: refillable, options: ['refill'] },
  {
    key: 'fightsfoodwaste',
    label: 'Fights food waste',
    src: fightsFoodWaste,
    options: ['fights-food-waste'],
  },
  {
    key: 'plasticfreeproducts',
    label: 'Plastic free products',
    src: plasticFree,
    options: ['plastic-free'],
  },
  { key: 'carbonmeasured', label: 'Carbon measured', src: carbonMeasured, options: ['carbon'] },
  { key: 'veganrange', label: 'Vegan range', src: vegan, options: ['vegan'] },
  { key: 'femaleled', label: 'Female led', src: femaleLed, options: ['female'] },
  {
    key: 'fairtradecertified',
    label: 'Fairtrade certified',
    src: fairtrade,
    options: ['fairtrade'],
  },
  { key: 'organicrange', label: 'Organic range', src: organic, options: ['organic'] },
  {
    key: 'waterlessproducts',
    label: 'Waterless products',
    src: waterless,
    options: ['waterless'],
  },
  { key: 'ukmade', label: 'UK made', src: ukMade, options: ['uk'] },
  {
    key: 'circulareconomy',
    label: 'Circular economy',
    src: circularEconomy,
    options: ['circular'],
  },
];

// Reduce anything to a comparable slug ("Vegan Range" / "vegan-range" /
// "veganRange" all become "veganrange") so we stay tolerant of formatting.
const normalize = value =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Build a lookup keyed by every token an ecolabel can be recognised by: its
// canonical key AND each real Sharetribe option value. This is what lets a
// stored value of `vegan` resolve to the `veganrange` badge.
const ECOLABEL_BY_NORMALIZED = ECOLABELS.reduce((acc, ecolabel) => {
  acc[normalize(ecolabel.key)] = ecolabel;
  (ecolabel.options || []).forEach(option => {
    acc[normalize(option)] = ecolabel;
  });
  return acc;
}, {});

// Values that explicitly mean "this label is crossed out / not applicable".
const NEGATIVE_VALUES = new Set(['false', 'no', 'n', 'off', '0', 'crossed', 'none']);

const isPositive = value => {
  if (value === true) {
    return true;
  }
  if (value == null || value === false) {
    return false;
  }
  return !NEGATIVE_VALUES.has(normalize(value));
};

/**
 * Work out which ecolabels apply to a listing from its extended (public) data.
 *
 * The labels that apply to a listing are ticked (and the rest crossed out) when
 * the listing is edited in Sharetribe. We don't hardcode the field key, because
 * it lives in the hosted listing-field config — instead we recognise the labels
 * by their slug wherever they appear. This supports both common storage shapes:
 *
 *   1. A multi-enum field holding the ticked slugs, e.g.
 *      `{ ecolabels: ['veganrange', 'organicrange'] }`
 *   2. Individual boolean fields keyed by slug, e.g.
 *      `{ veganrange: true, organicrange: false }`
 *
 * Only ticked labels are returned, always in the canonical ECOLABELS order.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @returns {Array<{key: string, label: string, src: string}>}
 */
export const getListingEcolabels = publicData => {
  if (!publicData || typeof publicData !== 'object') {
    return [];
  }

  const ticked = new Set();

  const considerValue = value => {
    const match = ECOLABEL_BY_NORMALIZED[normalize(value)];
    if (match && isPositive(value)) {
      ticked.add(match.key);
    }
  };

  // Primary, authoritative path: the hosted multi-enum `ecolabels` field. This
  // is the real field confirmed in the marketplace config, so when it's present
  // we trust it exclusively — that avoids short option slugs (e.g. `uk`, `vegan`)
  // accidentally matching values in unrelated fields.
  const primary = publicData.ecolabels;
  if (Array.isArray(primary) || typeof primary === 'string') {
    if (Array.isArray(primary)) {
      primary.forEach(considerValue);
    } else {
      considerValue(primary);
    }
    return ECOLABELS.filter(ecolabel => ticked.has(ecolabel.key));
  }

  // Fallback (no `ecolabels` field present): stay tolerant of other shapes so
  // the badges still work if the data is ever stored differently.
  Object.entries(publicData).forEach(([key, value]) => {
    // Shape: the field key itself is an ecolabel slug (boolean-ish value).
    const keyMatch = ECOLABEL_BY_NORMALIZED[normalize(key)];
    if (keyMatch && isPositive(value)) {
      ticked.add(keyMatch.key);
    }

    // Shape: some other multi-enum-style array holds the ecolabel slugs.
    if (Array.isArray(value)) {
      value.forEach(considerValue);
    }
  });

  return ECOLABELS.filter(ecolabel => ticked.has(ecolabel.key));
};

/**
 * Resolve a single stored/selected value (e.g. `vegan`, `veganrange`, `Vegan range`)
 * to its ecolabel definition, or undefined when it isn't one of ours.
 *
 * @param {string} value
 * @returns {{key: string, label: string, src: string, options: Array<string>}|undefined}
 */
export const getEcolabelByValue = value =>
  value == null ? undefined : ECOLABEL_BY_NORMALIZED[normalize(value)];

/**
 * Resolve a set of stored/selected values to ecolabel definitions.
 *
 * Duplicates are dropped and the result is always in the canonical ECOLABELS
 * order, so a collection gathered from several listings (e.g. everything a
 * seller has earned across their range) renders in the same order everywhere.
 *
 * @param {Array<string>} values
 * @returns {Array<{key: string, label: string, src: string}>}
 */
export const getEcolabelsByKeys = values => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const wanted = new Set(values.map(value => getEcolabelByValue(value)?.key).filter(Boolean));
  return ECOLABELS.filter(ecolabel => wanted.has(ecolabel.key));
};

/**
 * The canonical keys of the ecolabels awarded to a listing, e.g. ['veganrange', 'ukmade'].
 * Small and serialisable, so it can be stored in the basket and the order confirmation.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @returns {Array<string>}
 */
export const getListingEcolabelKeys = publicData =>
  getListingEcolabels(publicData).map(ecolabel => ecolabel.key);

/**
 * Count how many products in a basket or order carry each ecolabel.
 *
 * A product bought in two sizes is still one product, so entries are counted once
 * per id. Entries without known ecolabels (e.g. a basket saved before they were
 * stored) are left out rather than counted as having none.
 *
 * @param {Array<{id: string, ecolabelKeys: Array<string>}>} products
 * @returns {{productCount: number, ecolabels: Array<{ecolabel: Object, count: number}>}}
 *   ecolabels in canonical order, only those at least one product carries
 */
export const summariseProductEcolabels = products => {
  const keysById = new Map();
  (Array.isArray(products) ? products : []).forEach((product, index) => {
    if (product && Array.isArray(product.ecolabelKeys)) {
      keysById.set(product.id || `product-${index}`, product.ecolabelKeys);
    }
  });

  const productKeys = [...keysById.values()].map(keys =>
    getEcolabelsByKeys(keys).map(ecolabel => ecolabel.key)
  );
  const ecolabels = ECOLABELS.map(ecolabel => ({
    ecolabel,
    count: productKeys.filter(keys => keys.includes(ecolabel.key)).length,
  })).filter(({ count }) => count > 0);

  return { productCount: productKeys.length, ecolabels };
};

// The key the hosted multi-enum ecolabel field is expected to use. Only a
// fallback: findEcolabelFieldConfig prefers whatever the hosted config says.
const DEFAULT_ECOLABEL_FIELD_KEY = 'ecolabels';

/**
 * Find the hosted listing field that holds the ecolabels.
 *
 * The field lives in Sharetribe's hosted config, so rather than hardcoding a key
 * we recognise it: a multi-enum whose key looks like "ecolabels", or whose options
 * are ecolabels we have artwork for. That keeps the ecolabel filters working if the
 * field is ever renamed or relabelled in Console.
 *
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @returns {Object|undefined} the matching listing field config
 */
export const findEcolabelFieldConfig = listingFields => {
  if (!Array.isArray(listingFields)) {
    return undefined;
  }

  const isEcolabelKey = key => normalize(key || '').includes(DEFAULT_ECOLABEL_FIELD_KEY);
  const multiEnums = listingFields.filter(f => f?.schemaType === 'multi-enum');

  return (
    multiEnums.find(f => isEcolabelKey(f.key)) ||
    multiEnums.find(
      f => isEcolabelKey(f.filterConfig?.label) || isEcolabelKey(f.showConfig?.label)
    ) ||
    // Last resort: the multi-enum whose options we can draw badges for.
    multiEnums.find(
      f =>
        Array.isArray(f.enumOptions) &&
        f.enumOptions.length > 0 &&
        f.enumOptions.every(o => !!getEcolabelByValue(o.option))
    )
  );
};

/**
 * Is this single listing-field config the ecolabel field?
 *
 * @param {Object} fieldConfig a listing field config
 * @returns {boolean}
 */
export const isEcolabelFieldConfig = fieldConfig =>
  !!fieldConfig && findEcolabelFieldConfig([fieldConfig]) === fieldConfig;

/**
 * The ecolabels that can actually be filtered on, paired with the option value the
 * Marketplace API expects.
 *
 * When the hosted field is available we follow its options exactly (so we never
 * offer a filter value the API doesn't know about); otherwise we fall back to the
 * canonical list, using each ecolabel's first known option value.
 *
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @returns {{fieldKey: string, options: Array<{option: string, ecolabel: Object}>}}
 */
export const getEcolabelFilterOptions = listingFields => {
  const fieldConfig = findEcolabelFieldConfig(listingFields);
  const fieldKey = fieldConfig?.key || DEFAULT_ECOLABEL_FIELD_KEY;

  const options = Array.isArray(fieldConfig?.enumOptions)
    ? fieldConfig.enumOptions
        .map(({ option, label }) => {
          const ecolabel = getEcolabelByValue(option);
          return ecolabel ? { option, ecolabel, label: label || ecolabel.label } : null;
        })
        .filter(Boolean)
    : ECOLABELS.filter(ecolabel => ecolabel.options?.length > 0).map(ecolabel => ({
        option: ecolabel.options[0],
        ecolabel,
        label: ecolabel.label,
      }));

  return { fieldKey, options };
};

/**
 * The search mode used when the hosted field doesn't specify one.
 *
 * `has_any` means "show me products awarded any of the labels I ticked", which is
 * what multi-selecting badges is expected to do — and it matches the links the
 * homepage's "Shop by ecolabel" row already produces.
 */
export const ECOLABEL_FILTER_SEARCH_MODE = 'has_any';

/**
 * The search query param the ecolabel filter uses, e.g. `pub_ecolabels`.
 *
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @returns {string}
 */
export const getEcolabelSearchParamName = listingFields => {
  const { fieldKey } = getEcolabelFilterOptions(listingFields);
  const fieldConfig = findEcolabelFieldConfig(listingFields);
  return constructQueryParamName(fieldKey, fieldConfig?.scope || 'public');
};

/**
 * The search param value that finds products carrying any of the given ecolabels,
 * e.g. `{ pub_ecolabels: 'has_any:vegan,uk' }` — the shopper's priorities as a filter.
 *
 * @param {Array<string>} ecolabelKeys canonical ecolabel keys
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @returns {Object|null} null when none of the keys can be filtered on
 */
export const buildEcolabelSearchParams = (ecolabelKeys, listingFields) => {
  const { options } = getEcolabelFilterOptions(listingFields);
  const values = options
    .filter(({ ecolabel }) => (ecolabelKeys || []).includes(ecolabel.key))
    .map(({ option }) => option);
  return values.length > 0
    ? {
        [getEcolabelSearchParamName(listingFields)]: `${ECOLABEL_FILTER_SEARCH_MODE}:${values.join(
          ','
        )}`,
      }
    : null;
};

/**
 * Read an active ecolabel search param out of a set of search params.
 *
 * @param {Object} searchParams e.g. { pub_ecolabels: 'has_any:waterless,vegan' }
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @returns {{paramName: string, mode: string, keys: Array<string>}|null} null when
 *   no ecolabel filter is active
 */
export const parseEcolabelSearchParam = (searchParams, listingFields) => {
  const paramName = getEcolabelSearchParamName(listingFields);
  const rawValue = searchParams?.[paramName];

  if (typeof rawValue !== 'string' || rawValue.length === 0) {
    return null;
  }

  const mode = rawValue.startsWith('has_all:') ? 'has_all' : ECOLABEL_FILTER_SEARCH_MODE;
  const keys = rawValue
    .replace(/^has_(all|any):/, '')
    .split(',')
    .map(value => getEcolabelByValue(value.trim())?.key)
    .filter(Boolean);

  return keys.length > 0 ? { paramName, mode, keys } : null;
};

/**
 * Does a listing carry the ecolabels an active ecolabel filter asks for?
 *
 * Used as a safety net for the browse page: the Marketplace API can only filter on
 * a public-data key that's in its search schema, so if `ecolabels` isn't indexed the
 * API hands back every listing regardless of the filter. Checking the listing's own
 * public data means a ticked badge always narrows what the shopper sees.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @param {{mode: string, keys: Array<string>}} ecolabelSearch from parseEcolabelSearchParam
 * @returns {boolean}
 */
export const listingMatchesEcolabelSearch = (publicData, ecolabelSearch) => {
  const { mode, keys } = ecolabelSearch || {};
  if (!keys || keys.length === 0) {
    return true;
  }
  const awarded = getListingEcolabels(publicData).map(ecolabel => ecolabel.key);
  return mode === 'has_all'
    ? keys.every(key => awarded.includes(key))
    : keys.some(key => awarded.includes(key));
};

/**
 * A listing-field config for the ecolabel filter that is guaranteed to render on
 * the browse page.
 *
 * Whether a listing field shows up as a search filter normally depends entirely on
 * the hosted Sharetribe config (`showFilter`, `group`, and the category/listing-type
 * limits). Ecolabels are the reason shoppers come to ethy, so the browse page always
 * offers them: we start from the hosted field when it exists — keeping its key, scope
 * and option values so the query params stay valid — and otherwise fall back to the
 * canonical ECOLABELS list. Either way the filter is forced visible, primary, and
 * unrestricted by category or listing type, since an ecolabel can be awarded to any
 * product.
 *
 * @param {Array<Object>} listingFields config.listing.listingFields
 * @param {string} [fallbackLabel] the label to show shoppers
 * @returns {Object|undefined} a listing-field config, or undefined if there are no options
 */
export const buildEcolabelFilterFieldConfig = (
  listingFields,
  fallbackLabel = 'Shop by sustainability criteria'
) => {
  const hostedConfig = findEcolabelFieldConfig(listingFields);
  const { fieldKey, options } = getEcolabelFilterOptions(listingFields);

  if (options.length === 0) {
    return undefined;
  }

  const { filterConfig = {}, categoryConfig, listingTypeConfig, ...restOfHosted } =
    hostedConfig || {};

  return {
    ...restOfHosted,
    key: fieldKey,
    scope: hostedConfig?.scope || 'public',
    schemaType: 'multi-enum',
    enumOptions: options.map(({ option, label }) => ({ option, label })),
    filterConfig: {
      ...filterConfig,
      indexForSearch: true,
      showFilter: true,
      group: 'primary',
      // The marketplace's own wording wins over the hosted field's label: the hosted
      // one is phrased for the vendor filling the listing in ("Which sustainability
      // criteria does this product meet?"), which reads oddly to a shopper browsing.
      label: fallbackLabel || filterConfig.label,
      searchMode: filterConfig.searchMode || ECOLABEL_FILTER_SEARCH_MODE,
    },
  };
};
